'use client'

import * as React from 'react'
import { motion } from 'motion/react'

import { buildUrl } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { MediaType } from './types'

interface ContributorType {
  id?: string
  name?: string
  avatar?: MediaType | string
}

interface BookThemeType {
  id?: string
  name?: string
  icon?: MediaType | string
}

interface BookType {
  id?: string
  title?: string
  slug?: string
  synopsis?: string
  isbn?: string | null
  cover?: MediaType | string
  authors?: ContributorType[] | string[]
  illustrators?: ContributorType[] | string[]
  themes?: BookThemeType[] | string[]
}

interface BookShowcaseBlockFields {
  blockType: 'book-showcase'
  books?: BookType | string
}

interface BookShowcaseBlockProps {
  fields: BookShowcaseBlockFields
}

export function BookShowcaseBlock({ fields }: BookShowcaseBlockProps) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const book = typeof fields.books === 'string' ? null : fields.books!

  if (!book) {
    return (
      <div className='ui:my-6 ui:rounded-lg ui:border ui:p-8 ui:text-center ui:border-outline ui:bg-grey-10'>
        <p className='ui:text-grey-100'>Book not found or not expanded</p>
      </div>
    )
  }

  // Extract cover image
  const coverImage = typeof book.cover === 'string' ? null : book.cover

  // Extract authors (filter out string references)
  const authors = (book.authors ?? [])
    .filter((author): author is ContributorType => typeof author !== 'string')
    .filter((author) => author.name)

  // Extract illustrators (filter out string references)
  const illustrators = (book.illustrators ?? [])
    .filter((illustrator): illustrator is ContributorType => typeof illustrator !== 'string')
    .filter((illustrator) => illustrator.name)

  // Extract themes (filter out string references)
  const themes = (book.themes ?? [])
    .filter((theme): theme is BookThemeType => typeof theme !== 'string')
    .filter((theme) => theme.name)

  return (
    <motion.div
      className='ui:my-8'
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
    >
      <motion.a
        href={`/buku/${book.slug}`}
        target='_blank'
        className={cn(
          'ui:group ui:relative ui:block ui:overflow-hidden ui:rounded-2xl ui:border-2 ui:shadow-lg ui:transition-all ui:duration-300',
          'ui:hover:shadow-2xl ui:hover:shadow-blue-100/20',
          'ui:bg-neutral-white ui:border-blue-20',
        )}
        whileHover={{ y: -4 }}
        transition={{ duration: 0.3 }}
      >
        {/* Decorative gradient overlay */}
        <div className='ui:pointer-events-none ui:absolute ui:inset-0 ui:bg-linear-to-br ui:from-blue-100/5 ui:via-transparent ui:to-blue-100/10' />

        {/* Decorative corner accent */}
        <div className='ui:absolute ui:-right-8 ui:-top-8 ui:h-32 ui:w-32 ui:rounded-full ui:opacity-20 ui:blur-2xl ui:bg-blue-100' />
        <div className='ui:absolute ui:-bottom-6 ui:-left-6 ui:h-24 ui:w-24 ui:rounded-full ui:opacity-15 ui:blur-xl ui:bg-blue-70' />

        <div className='ui:relative ui:flex ui:flex-col ui:gap-6 ui:p-6 ui:md:flex-row ui:md:p-8'>
          {/* Book Cover Section */}
          {coverImage?.url && (
            <div className='ui:flex ui:shrink-0 ui:justify-center ui:md:justify-start'>
              <motion.div
                className='ui:relative ui:h-50 ui:w-35 ui:overflow-hidden ui:rounded-xl ui:shadow-xl ui:md:h-60 ui:md:w-40'
                whileHover={{ scale: 1.02, rotateY: -5 }}
                transition={{ duration: 0.3 }}
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Book spine effect */}
                <div className='ui:absolute ui:left-0 ui:top-0 ui:h-full ui:w-2 ui:bg-linear-to-r ui:from-neutral-black/20 ui:to-transparent' />
                <img
                  src={buildUrl('content', coverImage.url).href}
                  alt={coverImage.alt ?? book.title ?? 'Book cover'}
                  className='ui:h-full ui:w-full ui:object-cover'
                  loading='lazy'
                />
                {/* Glossy overlay */}
                <div className='ui:absolute ui:inset-0 ui:bg-linear-to-br ui:from-neutral-white/10 ui:via-transparent ui:to-neutral-black/10' />
              </motion.div>
            </div>
          )}

          {/* Book Details Section */}
          <div className='ui:flex ui:flex-1 ui:flex-col ui:justify-between'>
            {/* Title & Synopsis */}
            <div>
              <h4
                className={cn(
                  'ui:mb-3 ui:text-xl ui:font-bold ui:leading-tight ui:transition-colors ui:md:text-2xl',
                  'ui:text-neutral-black ui:group-hover:text-blue-100',
                )}
              >
                {book.title}
              </h4>

              {book.synopsis && (
                <p className='ui:mb-4 ui:line-clamp-3 ui:text-sm ui:leading-relaxed ui:text-grey-100'>
                  {book.synopsis}
                </p>
              )}
            </div>

            {/* Book Meta Information */}
            <div className='ui:space-y-3'>
              {/* Authors */}
              {authors.length > 0 && (
                <div className='ui:flex ui:items-center ui:gap-2'>
                  <span className='ui:shrink-0 ui:text-xs ui:font-semibold ui:uppercase ui:text-blue-100'>Penulis</span>
                  <span className='ui:text-sm ui:text-navy-100'>{authors.map((a) => a.name).join(', ')}</span>
                </div>
              )}

              {/* Illustrators */}
              {illustrators.length > 0 && (
                <div className='ui:flex ui:items-center ui:gap-2'>
                  <span className='ui:shrink-0 ui:text-xs ui:font-semibold ui:uppercase ui:text-blue-100'>
                    Ilustrator
                  </span>
                  <span className='ui:text-sm ui:text-navy-100'>{illustrators.map((i) => i.name).join(', ')}</span>
                </div>
              )}

              {/* Themes */}
              {themes.length > 0 && (
                <div className='ui:flex ui:flex-wrap ui:items-center ui:gap-2 ui:pt-2'>
                  {themes.map((theme, index) => {
                    const themeIcon = typeof theme.icon === 'string' ? null : theme.icon
                    return (
                      <span
                        key={theme.id ?? index}
                        className={cn(
                          'ui:inline-flex ui:items-center ui:gap-1.5 ui:rounded-full ui:border ui:px-3 ui:py-1 ui:text-xs ui:font-medium',
                          'ui:bg-blue-10 ui:border-blue-20 ui:text-blue-100',
                        )}
                      >
                        {themeIcon?.url && (
                          <img
                            src={themeIcon.url}
                            alt={theme.name}
                            className='ui:h-3.5 ui:w-3.5 ui:object-contain ui:rounded-full'
                          />
                        )}
                        {theme.name}
                      </span>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom accent bar */}
        <div
          className={cn(
            'ui:h-1 ui:w-full ui:bg-linear-to-r ui:from-blue-100 ui:via-blue-70 ui:to-blue-50',
            'ui:opacity-60 ui:transition-opacity ui:group-hover:opacity-100',
          )}
        />
      </motion.a>
    </motion.div>
  )
}
